import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api';
import { useSiteContent } from '../hooks/useSiteContent';
import { useAuth } from '../contexts/AuthContext';

type Category = 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';

interface Subject {
  id: string;
  name: string;
  category: Category;
  isActive: boolean;
}

type Level = 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';
type ParentOrStudent = 'Parent' | 'Student';
type Format = 'Online' | 'In person' | 'Either';

const LEVEL_OPTIONS: { value: Level; label: string }[] = [
  { value: 'ELEVEN_PLUS', label: '11+' },
  { value: 'GCSE', label: 'GCSE' },
  { value: 'A_LEVEL', label: 'A-level' },
  { value: 'UNIVERSITY', label: 'Oxbridge' },
];

const YEAR_GROUPS = [
  'Year 4',
  'Year 5',
  'Year 6',
  'Year 7',
  'Year 8',
  'Year 9',
  'Year 10',
  'Year 11',
  'Year 12',
  'Year 13',
  'Other',
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Short logged-in variant: shows just a message textarea and posts to
 * /messages/authed. The full 3-step flow below is for anonymous leads.
 */
const LoggedInContactForm: React.FC<{ userName: string }> = ({ userName }) => {
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (message.trim().length === 0) {
      setError('Please write a message.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/messages/authed', { message: message.trim() });
      setSent(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.response?.data?.message || 'Could not send. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="rule-top text-center">
        <p className="mt-6 text-body text-ink font-serif">
          Message sent. Ethan will reply by email within 24 hours.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <Link to="/dashboard" className="btn btn-primary">Back to dashboard</Link>
          <Link to="/chat" className="btn btn-secondary">Open chat</Link>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-6 max-w-2xl">
      <p className="text-body text-ink/90">
        Hi {userName} — you're signed in, so no need to fill in your details again. Just tell Ethan what's on your mind and he'll reply by email.
      </p>
      <p className="text-sm text-muted">
        For ongoing back-and-forth, the <Link to="/chat" className="text-ochre hover:underline">chat</Link> is usually quicker.
      </p>
      {error && (
        <div className="border border-rule bg-paper p-4">
          <p className="text-ink text-[15px]">{error}</p>
        </div>
      )}
      <div>
        <label htmlFor="message" className="block text-[15px] font-medium text-ink mb-2">
          Message
        </label>
        <textarea
          id="message"
          rows={8}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="input"
          placeholder="What's on your mind? Anything from scheduling to a specific question about a lesson."
          required
        />
      </div>
      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={busy}
          className="btn btn-primary disabled:opacity-50"
        >
          {busy ? 'Sending…' : 'Send message'}
        </button>
      </div>
    </form>
  );
};

const ContactPage: React.FC = () => {
  const c = useSiteContent();
  const { user } = useAuth();

  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1
  const [yearGroup, setYearGroup] = useState<string>('');
  const [level, setLevel] = useState<Level | ''>('');
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);

  // Step 2
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<ParentOrStudent | ''>('');

  // Step 3
  const [message, setMessage] = useState('');
  const [format, setFormat] = useState<Format | ''>('');

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Subjects
  const [subjects, setSubjects] = useState<Subject[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/subjects');
        if (cancelled) return;
        setSubjects(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setSubjects([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Reset subject selection if level changes
  useEffect(() => {
    setSelectedSubjects([]);
  }, [level]);

  const filteredSubjects = useMemo(() => {
    if (!level) return [];
    return subjects.filter((s) => s.category === level && s.isActive !== false);
  }, [subjects, level]);

  const toggleSubject = (name: string) => {
    setSelectedSubjects((prev) =>
      prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]
    );
  };

  const step1Valid = !!level;
  const step2Valid = name.trim().length > 0 && EMAIL_RE.test(email.trim());
  const step3Valid = message.trim().length > 0;

  const levelLabel = (l: Level | ''): string => {
    const found = LEVEL_OPTIONS.find((o) => o.value === l);
    return found ? found.label : '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!step3Valid || !step2Valid || !step1Valid) return;

    setError('');
    setSubmitting(true);

    const preambleLines = [
      yearGroup ? `Year group: ${yearGroup}` : null,
      level ? `Level: ${levelLabel(level)}` : null,
      format ? `Preferred format: ${format}` : null,
      role ? `${role}` : null,
      selectedSubjects.length ? `Subjects: ${selectedSubjects.join(', ')}` : null,
    ].filter(Boolean);

    const combinedMessage =
      (preambleLines.length ? preambleLines.join('\n') + '\n\n' : '') + message.trim();

    try {
      await api.post('/messages', {
        name: name.trim(),
        age: '',
        email: email.trim(),
        phoneNumber: phone.trim(),
        subjects: selectedSubjects,
        message: combinedMessage,
      });
      setSubmitted(true);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = () => {
    setError('');
    if (step === 1 && step1Valid) setStep(2);
    else if (step === 2 && step2Valid) setStep(3);
  };

  const goBack = () => {
    setError('');
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
  };

  return (
    <main className="bg-cream">
      <Helmet>
        <title>Book an intro call — EJL Education</title>
        <meta name="description" content="Tell me what you or your child are working on. I'll reply within 24 hours." />
      </Helmet>
      <section className="section">
        <div className="container-content">
          <h1 className="mb-4">{c('contact_heading', 'Book a £5 20-minute call')}</h1>
          <p className="text-muted max-w-2xl text-body">
            {c(
              'contact_subhead',
              "Tell me what your child (or you) is working on. I'll reply within 24 hours."
            )}
          </p>
        </div>
      </section>

      <section className="border-t border-rule py-16 md:py-20">
        <div className="container-content">
          <div className="max-w-2xl">
            {user ? (
              <LoggedInContactForm userName={user.name.split(' ')[0] || user.name} />
            ) : submitted ? (
              <div className="rule-top text-center">
                <p className="mt-6 text-body text-ink font-serif">
                  {c('contact_thanks', "Thanks, I'll be in touch within 24 hours.")}
                </p>
              </div>
            ) : (
              <>
                {/* Progress indicator */}
                <div className="mb-10">
                  <div
                    className="grid grid-cols-3 gap-2 mb-3"
                    role="progressbar"
                    aria-valuemin={1}
                    aria-valuemax={3}
                    aria-valuenow={step}
                  >
                    {[1, 2, 3].map((n) => (
                      <div
                        key={n}
                        className={`h-[2px] ${n === step ? 'bg-ochre' : 'bg-rule'}`}
                      />
                    ))}
                  </div>
                  <p className="eyebrow">Step {step} of 3</p>
                </div>

                {error && (
                  <div className="mb-6 border border-rule bg-paper p-4">
                    <p className="text-ink text-[15px]">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate>
                  {step === 1 && (
                    <div className="space-y-8">
                      <h2 className="mb-2">What are you looking for?</h2>

                      <div>
                        <label
                          htmlFor="yearGroup"
                          className="block text-[15px] font-medium text-ink mb-2"
                        >
                          Year group
                        </label>
                        <select
                          id="yearGroup"
                          value={yearGroup}
                          onChange={(e) => setYearGroup(e.target.value)}
                          className="input"
                        >
                          <option value="">Select year group</option>
                          {YEAR_GROUPS.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </div>

                      <fieldset>
                        <legend className="block text-[15px] font-medium text-ink mb-3">
                          Level
                        </legend>
                        <div className="space-y-2">
                          {LEVEL_OPTIONS.map((opt) => (
                            <label
                              key={opt.value}
                              className="flex items-center gap-3 cursor-pointer"
                            >
                              <input
                                type="radio"
                                name="level"
                                value={opt.value}
                                checked={level === opt.value}
                                onChange={() => setLevel(opt.value)}
                                className="text-ochre focus:ring-ochre"
                              />
                              <span className="text-body text-ink font-serif">
                                {opt.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      </fieldset>

                      {level && (
                        <fieldset>
                          <legend className="block text-[15px] font-medium text-ink mb-3">
                            Subjects
                          </legend>
                          {filteredSubjects.length === 0 ? (
                            <p className="text-muted text-body italic">
                              (No subjects listed for this level yet. Leave blank and
                              mention what you need in step 3.)
                            </p>
                          ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                              {filteredSubjects.map((s) => (
                                <label
                                  key={s.id}
                                  className="flex items-center gap-3 border-t border-rule py-3 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedSubjects.includes(s.name)}
                                    onChange={() => toggleSubject(s.name)}
                                    className="text-ochre focus:ring-ochre"
                                  />
                                  <span className="text-body text-ink font-serif">
                                    {s.name}
                                  </span>
                                </label>
                              ))}
                            </div>
                          )}
                        </fieldset>
                      )}

                      <div className="flex flex-col sm:flex-row gap-3 sm:justify-end pt-4">
                        <button
                          type="button"
                          onClick={goNext}
                          disabled={!step1Valid}
                          className="btn btn-primary w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next →
                        </button>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-8">
                      <h2 className="mb-2">Who are you?</h2>

                      <div>
                        <label
                          htmlFor="name"
                          className="block text-[15px] font-medium text-ink mb-2"
                        >
                          Name
                        </label>
                        <input
                          id="name"
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="input"
                          autoComplete="name"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="email"
                          className="block text-[15px] font-medium text-ink mb-2"
                        >
                          Email
                        </label>
                        <input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="input"
                          autoComplete="email"
                          required
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="phone"
                          className="block text-[15px] font-medium text-ink mb-2"
                        >
                          Phone (optional)
                        </label>
                        <input
                          id="phone"
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="input"
                          autoComplete="tel"
                        />
                      </div>

                      <fieldset>
                        <legend className="block text-[15px] font-medium text-ink mb-3">
                          Are you the parent or student?
                        </legend>
                        <div className="space-y-2">
                          {(['Parent', 'Student'] as ParentOrStudent[]).map((r) => (
                            <label
                              key={r}
                              className="flex items-center gap-3 cursor-pointer"
                            >
                              <input
                                type="radio"
                                name="role"
                                value={r}
                                checked={role === r}
                                onChange={() => setRole(r)}
                                className="text-ochre focus:ring-ochre"
                              />
                              <span className="text-body text-ink font-serif">{r}</span>
                            </label>
                          ))}
                        </div>
                      </fieldset>

                      <div className="flex flex-col sm:flex-row gap-3 sm:justify-between pt-4">
                        <button
                          type="button"
                          onClick={goBack}
                          className="btn btn-secondary w-full sm:w-auto"
                        >
                          Back
                        </button>
                        <button
                          type="button"
                          onClick={goNext}
                          disabled={!step2Valid}
                          className="btn btn-primary w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next →
                        </button>
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-8">
                      <h2 className="mb-2">Tell me more</h2>

                      <div>
                        <label
                          htmlFor="message"
                          className="block text-[15px] font-medium text-ink mb-2"
                        >
                          Message
                        </label>
                        <textarea
                          id="message"
                          rows={6}
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          className="input"
                          placeholder="What are you working towards? What's been difficult? Any deadlines?"
                          required
                        />
                      </div>

                      <fieldset>
                        <legend className="block text-[15px] font-medium text-ink mb-3">
                          Preferred session format
                        </legend>
                        <div className="space-y-2">
                          {(['Online', 'In person', 'Either'] as Format[]).map((f) => (
                            <label
                              key={f}
                              className="flex items-center gap-3 cursor-pointer"
                            >
                              <input
                                type="radio"
                                name="format"
                                value={f}
                                checked={format === f}
                                onChange={() => setFormat(f)}
                                className="text-ochre focus:ring-ochre"
                              />
                              <span className="text-body text-ink font-serif">{f}</span>
                            </label>
                          ))}
                        </div>
                      </fieldset>

                      <div className="flex flex-col sm:flex-row gap-3 sm:justify-between pt-4">
                        <button
                          type="button"
                          onClick={goBack}
                          className="btn btn-secondary w-full sm:w-auto"
                        >
                          Back
                        </button>
                        <button
                          type="submit"
                          disabled={submitting || !step3Valid}
                          className="btn btn-primary w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {submitting ? 'Sending…' : 'Send'}
                        </button>
                      </div>
                    </div>
                  )}
                </form>
              </>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
